// =============================================================================
// /ai-transformation - the AI transformation page
// -----------------------------------------------------------------------------
// Who reads it: PE-backed and mid-market companies that ran an AI pilot and
// have nothing in daily use, advisory and professional services firms, public
// sector programs, and PE deal teams looking at AI targets (secondary). They
// arrive sceptical, so the page frames the gap before it makes the offer.
//
// Page spine (long-form story, one offer, ONE action): hero -> the gap -> what
// we transform -> assess, build, adopt -> why Restack (tabs) -> who it is for
// -> transformation work behind Restack -> questions -> the form. Both "Book a
// scoping call" controls land on the same form.
//
// Built from the landing page's own pieces (hero classes, Section, Container,
// SectionEyebrow, SectionTitle, StatCard, BaseCard, AnimateOnScroll, the FAQ
// and closing-band treatments) so a visitor moving between the two reads one
// site. The landing page does not number its sections, so neither does this.
//
// Honesty rules, do not "improve" them away:
//   - No client logos, testimonials, case studies of Restack clients, or
//     "trusted by" strip. The transformation work below was Roland Berger's,
//     Cognizant's and KBBO's client work, led by Restack's founder: text only,
//     never a client or former-employer logo, and always under the
//     founder-attribution subtitle.
//   - No investor, funding, or partnership claims. The only third-party logo
//     is Microsoft's AppSource badge, linking to the listing.
//   - No team size, offices, or headcount. "We" is fine; "our engineers",
//     "our consultants", and "a team of" are not.
//   - No measured-results claims (speed-ups, savings, ROI).
//   - The only figures are the brief's: 3 agents, 60+ interviews, 10+ pilot
//     evaluations, 52 firms, $80M, 7 residency categories, 8,000+ users, 18
//     companies, ~$1B, ~3M customers, $8B, 7,000+ merchants. Durations are
//     written out in words, and program dates are left off.
//
// House style: no em dashes; sentence case headings; every motion is disabled
// under prefers-reduced-motion.
// =============================================================================

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import NorthEastRounded from '@mui/icons-material/NorthEastRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import ManageSearchRounded from '@mui/icons-material/ManageSearchRounded';
import AccountTreeRounded from '@mui/icons-material/AccountTreeRounded';
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded';
import QueryStatsRounded from '@mui/icons-material/QueryStatsRounded';
import PolicyRounded from '@mui/icons-material/PolicyRounded';
import AppsRounded from '@mui/icons-material/AppsRounded';
import InsertDriveFileRounded from '@mui/icons-material/InsertDriveFileRounded';
import InsightsRounded from '@mui/icons-material/InsightsRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';
import RequestQuoteRounded from '@mui/icons-material/RequestQuoteRounded';

import SEO from '../../shared/components/SEO';
import { SiteHeader } from '../../shared/components/SiteHeader';
import { Footer } from '../../shared/components/Footer';
import { OfficeHeroStage } from '../../shared/components/OfficeHeroStage';
import { HeroDecoration } from '../../shared/components/HeroDecoration';
import { AppSourceBadge } from '../../shared/components/AppSourceBadge';
import { useScrollReveal } from '../../shared/components/useScrollReveal';
import {
  LandingContainer,
  Section,
  Container,
  SectionEyebrow,
  SectionTitle,
  SectionSubtitle,
  CenteredHeader,
  AnimateOnScroll,
  BaseCard,
  StatCard,
  StatValue,
  StatLabel,
  StatSublabel,
  landingHeroStyles,
} from '../home/HomePage.styles';
import '../home/HomePage.styles.css';
import { buildFunctionUrl } from '../../shared/utils';

// Marketing palette, identical to the landing page.
const ACCENT = '#B83B1D';
const INK = '#2E140A';
const PAPER = '#FAF9F7';
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const inkA = (o) => alpha(INK, o);
const accentA = (o) => alpha(ACCENT, o);

// One reduced-motion guard, spread into anything that moves.
const STILL = { transition: 'none', animation: 'none', transform: 'none' };

// BaseCard lifts on hover, which tells a reader it is clickable. Cards that
// are not links hold still: this restates BaseCard's resting light-mode look.
const STATIC_CARD = {
  '&:hover': {
    transform: 'none',
    boxShadow: 'none',
    borderColor: 'rgba(0, 0, 0, 0.06)',
    background: 'rgba(255, 255, 255, 0.75)',
  },
};

// The landing hero clears a 40px announcement banner this page does not run;
// without it the headline would sit a banner's height too low.
const HERO_CSS = `
.aitx-hero {
  padding-top: clamp(98px, 9.5vh, 134px);
}
`;

// -----------------------------------------------------------------------------
// Content. The copy is the briefs', verbatim. Do not paraphrase it.
// -----------------------------------------------------------------------------

// Published work the proof cards point at. A card with no URL renders as a
// plain card rather than a link, so a missing URL can never ship a dead link.
const LINKS = {
  adoptionMap: null,
  aiTargets: null,
  unleashedDemo: null,
};

const HERO_SUBHEAD =
  'Restack picks the workflows where AI pays, builds AI agents into the tools your teams already use, and gets people using them.';

const HERO_PROOF = [
  { value: '3', label: 'AI agents live on Microsoft AppSource' },
  { value: '60+', label: 'PE and consulting leaders interviewed on AI adoption' },
  { value: '10+', label: 'enterprise pilot evaluations' },
];

const GAP_BODY =
  'We interviewed more than 60 partners and deal-team leaders at PE and consulting firms. AI showed up in research and first drafts. Then people copied the work by hand into Excel and PowerPoint. Of 52 firms, none had AI working inside the files themselves. The models can do more than most firms are using them for, and the gap sits in how the work gets done.';

// The two figures the gap paragraph rests on. Both are stated in it.
const GAP_FIGURES = [
  { value: '60+', label: 'Partners and deal-team leaders interviewed', sub: 'At PE and consulting firms' },
  { value: '52', label: 'Firms in the interviews', sub: 'None had AI working inside the files' },
];

const TRANSFORM = [
  {
    Icon: DescriptionRounded,
    title: 'Documents and analysis',
    body: 'Drafts, models and memos built inside the Office files your teams already use.',
  },
  {
    Icon: ManageSearchRounded,
    title: 'Knowledge search',
    body: 'Answers from your own documents and data, with the source behind every answer.',
  },
  {
    Icon: AccountTreeRounded,
    title: 'Operations workflows',
    body: 'Intake, triage, reconciliation and reporting handled by agents, with a person signing off.',
  },
  {
    Icon: SupportAgentRounded,
    title: 'Customer and service work',
    body: 'First-line replies, case summaries and handovers.',
  },
  {
    Icon: QueryStatsRounded,
    title: 'Finance and reporting',
    body: 'Monthly packs, variance commentary and board decks.',
  },
  {
    Icon: PolicyRounded,
    title: 'AI governance and enablement',
    body: 'Usage policies, model choices, and training so teams use AI safely.',
  },
];

const PHASES = [
  {
    name: 'Assess',
    meta: 'Two to three weeks, fixed fee',
    body: 'We map your workflows, rank them by value and feasibility, and agree the two or three to build first.',
  },
  {
    name: 'Build',
    meta: 'A fixed-scope sprint',
    body: 'We build or configure AI agents for those workflows inside the tools your teams already use: PowerPoint, Excel, Word and your data sources.',
  },
  {
    name: 'Adopt',
    meta: 'Until it runs without us',
    body: 'We train the teams, measure usage and fix what breaks until the workflow runs without us. Ongoing support is available as a fractional AI lead.',
  },
];

const WHY = [
  {
    Icon: AppsRounded,
    title: 'We build the systems ourselves.',
    body: "Restack's own AI agents run inside PowerPoint, Excel and Word and are live on Microsoft AppSource after Microsoft's review.",
    visual: 'agents',
  },
  {
    Icon: InsertDriveFileRounded,
    title: 'We work where the work happens.',
    body: 'Agents sit inside the files and tools people already use, so nobody has to switch apps.',
    visual: 'inFile',
  },
  {
    Icon: InsightsRounded,
    title: 'We know where adoption breaks.',
    body: 'Our interviews with 60+ firm leaders mapped where AI stalls in professional work. We design around those points from the first week.',
    visual: 'adoption',
  },
  {
    Icon: TuneRounded,
    title: 'We pick the model for the task.',
    body: 'Claude, GPT and open models, chosen per workflow, with no lock-in to one vendor.',
    visual: 'models',
  },
  {
    Icon: RequestQuoteRounded,
    title: 'One fixed fee per phase.',
    body: 'You get the number before work starts. It changes only if the scope does.',
    visual: 'fee',
  },
];

const AUDIENCES = [
  { title: 'PE-backed companies', body: 'An AI plan for the first 100 days, built and in use.' },
  { title: 'Mid-market companies', body: 'The first AI workflows that pay back, without hiring an AI team.' },
  { title: 'Advisory firms', body: 'An AI workstream delivered inside your client engagement.' },
  { title: 'Professional services firms', body: 'Agents built into the documents, research and reporting your people produce.' },
  { title: 'Public-sector programs', body: 'AI programs taken from strategy to a governed rollout.' },
];

// The founder's earlier programs. Each was another firm's client work, which
// is why the section subtitle attributes it and no card carries a logo.
const TRACK_RECORD_SUBTITLE =
  "Programs led by Restack's founder at Roland Berger's AI practice and earlier at Cognizant and KBBO.";

const ROLAND_BERGER_WORK = [
  {
    name: 'ALLaM',
    scale: "Saudi Arabia's national Arabic LLM",
    work: 'Took the model from research to a governed deployment plan on Azure: API access model, evaluation and RAG frameworks, governance, rollout plan.',
  },
  {
    name: 'Government AI programs at SCAI (now Humain)',
    scale: 'National AI programs',
    work: 'Product lead: PRDs and roadmap for national AI programs.',
  },
  {
    name: 'Saudi Premium Residency platform',
    scale: '7 residency categories',
    work: "Product design across seven residency categories; presented to the Center's CEO and accepted.",
  },
  {
    name: "Qatar's national digital and AI skills strategy (MCIT)",
    scale: 'National strategy',
    work: 'Led the strategy that launched the Qatar Digital Academy.',
  },
  {
    name: 'AI acquisition',
    scale: '$80M',
    work: 'Buy-side diligence for a sovereign-backed AI group.',
  },
];

const EARLIER_WORK = [
  {
    org: 'Cognizant',
    name: 'WPP',
    scale: '8,000+ users across 18 operating companies',
    work: 'A financial ERP rolled out with change management.',
  },
  {
    org: 'Cognizant',
    name: 'MG Motor',
    scale: '~$1B in digital sales',
    work: 'E-commerce launch.',
  },
  {
    org: 'Cognizant',
    name: 'First Abu Dhabi Bank merger',
    scale: '~3M customers',
    work: 'Product continuity through the merger.',
  },
  {
    org: 'Product lead',
    name: 'KBBO, an $8B investment group',
    scale: '7,000+ merchants',
    work: 'Q-commerce taken from zero.',
  },
];

const PUBLISHED = [
  {
    kind: 'Research',
    title: 'The AI adoption map',
    body: 'Where AI stalls in professional work, from interviews with 60+ PE and consulting leaders.',
    action: 'Read',
    href: LINKS.adoptionMap,
  },
  {
    kind: 'Framework',
    title: 'Due diligence of AI targets',
    body: 'Five checks for an AI acquisition: usage, model layer, data, inference economics, integration.',
    action: 'Read',
    href: LINKS.aiTargets,
  },
  {
    kind: 'Podcast',
    title: 'Live, unedited demo',
    body: 'Restack at work on the Unleashed podcast, recorded live with no cuts.',
    action: 'Listen',
    href: LINKS.unleashedDemo,
  },
];

// `answer` is the plain text the FAQPage schema carries. `link` optionally
// appends an internal link to the rendered answer only.
const FAQ = [
  {
    question: 'What does it cost?',
    answer: 'One fixed fee per phase, agreed before work starts. The assessment is a two-to-three-week fixed-fee project.',
  },
  {
    question: 'How long until something is live?',
    answer: "The assessment takes two to three weeks. We agree the build sprint's length during the assessment, workflow by workflow.",
  },
  {
    question: 'Which models do you use?',
    answer: "Claude, GPT and open models, picked per task. We aren't tied to one vendor.",
  },
  {
    question: 'Do you work inside our existing tools?',
    answer: 'Yes. Microsoft 365 (PowerPoint, Excel, Word) and the data sources your teams already use.',
  },
  {
    question: 'How do you handle our data?',
    answer: "We work within your security requirements and use model providers' zero-data-retention options where they exist.",
    link: { lead: 'More on our ', label: 'security page', to: '/security' },
  },
  {
    question: 'Do you advise or build?',
    answer: 'Both. The assessment says what to build; the sprint builds it; the adoption phase gets it used.',
  },
  {
    question: 'Do you do AI due diligence for deals?',
    answer: 'Yes, for PE deal teams, using our five-check framework: usage, model layer, data, inference economics, integration.',
  },
  {
    question: 'Where do you work?',
    answer: 'Remotely, from Toronto.',
  },
  {
    question: "What if we're not ready to commit?",
    answer: 'Ask for a free two-page note on where AI could pay in your business.',
  },
];

const SEO_TITLE = 'AI transformation for PE-backed and mid-market companies | Restack';
const SEO_DESCRIPTION =
  'Restack picks the workflows where AI pays, builds AI agents into the tools your teams already use, and gets people using them. Fixed fee per phase.';

// Service + FAQPage structured data, built from the same entries the page
// renders so an answer engine quotes what a reader actually sees.
const PAGE_SCHEMA = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Restack AI Transformation',
    serviceType: 'AI transformation',
    description: SEO_DESCRIPTION,
    provider: { '@type': 'Organization', name: 'Restack', url: 'https://re-stack.com' },
    areaServed: 'Global',
    audience: {
      '@type': 'BusinessAudience',
      audienceType: AUDIENCES.map(({ title }) => title).join(', '),
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  },
];

/**
 * Per-field validation. Specific beats polite: say what to do, not "invalid".
 * Every field is required: the same-day reply needs to know who is asking,
 * where they work, and where AI is stuck.
 */
function validateEnquiry({ name, email, company, stuck }) {
  const errors = {};
  if (!name) errors.name = 'Add your name so we know who we are replying to.';
  if (!email) errors.email = 'Add your work email so we can reply.';
  else if (!EMAIL_RE.test(email)) errors.email = 'That does not look like an email address. Check for a typo.';
  if (!company) errors.company = 'Add your company so we can prepare for the call.';
  if (!stuck) errors.stuck = 'A line or two is enough. It is what we reply to.';
  return errors;
}

const FIELD_ORDER = ['name', 'email', 'company', 'stuck'];

// =============================================================================

export default function AITransformationPage() {
  const theme = useTheme();
  const isVisible = useScrollReveal();
  const location = useLocation();
  // One stable holder for the four inputs, so the handlers below can focus a
  // field without depending on it.
  const fieldRefs = useRef({});
  const bindField = (key) => (el) => { fieldRefs.current[key] = el; };

  const [form, setForm] = useState({ name: '', email: '', company: '', stuck: '' });
  const [status, setStatus] = useState('idle'); // idle | submitting | sent | error
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const onField = (key) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [key]: value }));
    // Clear the error as soon as they start fixing it.
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  // The page's one destination. The hero CTA lands here.
  const goToForm = useCallback(() => {
    const el = document.getElementById('form');
    if (!el) return;
    scrollToElement(el, {
      onDone: () => fieldRefs.current.name?.focus({ preventScroll: true }),
    });
  }, []);

  // /ai-transformation#form is a deep link worth sending in an email, and the
  // old /advisory#form links redirect here with the hash intact.
  useEffect(() => {
    if (location.hash !== '#form') return undefined;
    const t = setTimeout(goToForm, 60);
    return () => clearTimeout(t);
  }, [location.hash, location.key, goToForm]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const values = {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        stuck: form.stuck.trim(),
      };

      const errors = validateEnquiry(values);
      if (Object.keys(errors).length) {
        setFieldErrors(errors);
        setSubmitError('');
        setStatus('idle');
        // Focus the first field that needs fixing, in reading order.
        const first = FIELD_ORDER.find((key) => errors[key]);
        fieldRefs.current[first]?.focus();
        return;
      }

      setFieldErrors({});
      setSubmitError('');
      setStatus('submitting');
      try {
        const res = await fetch(buildFunctionUrl('sendFeedback', { forceProduction: true }), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            feedbackType: 'AI transformation enquiry',
            userName: values.name,
            userEmail: values.email,
            feedback: [
              'New AI transformation enquiry from /ai-transformation.',
              '',
              `Name: ${values.name}`,
              `Work email: ${values.email}`,
              `Company: ${values.company}`,
              '',
              'Where AI is stuck:',
              values.stuck,
            ].join('\n'),
          }),
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        setStatus('sent');
      } catch (err) {
        setSubmitError('That did not send. Email hi@re-stack.com and we will pick it up there.');
        setStatus('error');
      }
    },
    [form],
  );

  // ---------------------------------------------------------------------------
  // Shared type, matched to the landing page's own cards and lists.
  // ---------------------------------------------------------------------------
  const font = theme.typography.fontFamily;
  const hair = alpha(theme.palette.text.primary, 0.08);
  const cardTitleSx = {
    fontFamily: font,
    fontWeight: 600,
    fontSize: { xs: '1rem', md: '1.125rem' },
    lineHeight: 1.35,
    color: 'text.primary',
    m: 0,
    mb: 1,
  };
  const cardBodySx = {
    fontFamily: font,
    fontSize: { xs: '0.8125rem', md: '0.875rem' },
    lineHeight: { xs: 1.55, md: 1.6 },
    color: 'text.secondary',
    m: 0,
  };
  const groupLabelSx = {
    fontFamily: font,
    fontSize: '0.8125rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: inkA(0.55),
    textAlign: 'center',
    m: 0,
    mb: { xs: 2, md: 2.5 },
  };
  const chipSx = {
    flexShrink: 0,
    width: { xs: 40, md: 44 },
    height: { xs: 40, md: 44 },
    borderRadius: '12px',
    backgroundColor: accentA(0.1),
    color: ACCENT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <LandingContainer data-theme="light" sx={{ minHeight: '100vh' }}>
      <SEO
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        canonical="/ai-transformation"
        breadcrumbLabel="AI Transformation"
        schema={PAGE_SCHEMA}
      />

      {/* The landing hero's stylesheet, then this page's one override. */}
      <style>{landingHeroStyles}</style>
      <style>{HERO_CSS}</style>

      <SiteHeader />

      <Box component="main" id="main-content">
        {/* ================================================================
            HERO. The landing page's own treatment: centred headline with the
            accent gradient, subhead, one dark pill, and the Office stage.
            ================================================================ */}
        <Box component="section" className="landing-hero aitx-hero" aria-labelledby="aitx-title">
          <HeroDecoration />

          <Box className="landing-hero__content" sx={{ '&.landing-hero__content': { pt: { md: '7vh' } } }}>
            <Box className="landing-hero__headline-wrapper">
              <Typography variant="h1" id="aitx-title" className="landing-hero__headline">
                From AI pilot to <span className="landing-hero__headline-accent">daily work.</span>
              </Typography>
            </Box>
            <Typography
              sx={{
                maxWidth: { xs: '92%', sm: 600, md: 720 },
                mx: 'auto',
                mt: { xs: 1.5, md: 2.5 },
                textAlign: 'center',
                fontFamily: font,
                fontSize: { xs: '0.9rem', md: '1.05rem' },
                fontWeight: 400,
                lineHeight: 1.55,
                letterSpacing: '-0.01em',
                color: alpha(theme.palette.text.primary, 0.55),
              }}
            >
              {HERO_SUBHEAD}
            </Typography>
          </Box>

          <Box
            className="landing-hero__cta-row"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              textAlign: 'center',
              px: 2,
              pt: { xs: '40px', md: '34px' },
              pb: { xs: 3, md: 1 },
            }}
          >
            <PrimaryCta onClick={goToForm} />
            <Box
              component="ul"
              aria-label="Restack in numbers"
              sx={{
                listStyle: 'none',
                m: 0,
                p: 0,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: 0.5, md: 0 },
              }}
            >
              {HERO_PROOF.map(({ value, label }, i) => (
                <Box
                  component="li"
                  key={label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    fontFamily: font,
                    fontSize: { xs: '0.78rem', md: '0.8125rem' },
                    fontWeight: 500,
                    letterSpacing: '-0.005em',
                    lineHeight: 1.4,
                    color: alpha(theme.palette.text.primary, 0.6),
                    // Dot dividers on the single desktop row, as the landing
                    // trust strip does.
                    '&::before': i === 0 ? {} : {
                      content: '""',
                      display: { xs: 'none', md: 'block' },
                      width: 3,
                      height: 3,
                      mx: 1.5,
                      borderRadius: '50%',
                      backgroundColor: alpha(theme.palette.text.primary, 0.3),
                    },
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 700, color: 'text.primary', mr: 0.5 }}>{value}</Box>
                  {label}
                </Box>
              ))}
            </Box>
          </Box>

          {/* The stage is CSS animation and decorative markup, so it is one
              image to assistive technology. order 3 keeps it below the CTA
              row once the hero's tablet and phone rules order its children. */}
          <Box
            role="img"
            aria-label="Restack's agent building a market sizing slide inside PowerPoint, pulling figures from an Excel model and a Word brief."
            sx={{ order: 3, width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            <OfficeHeroStage />
          </Box>
        </Box>

        {/* ================================================================
            THE GAP
            ================================================================ */}
        <Section id="gap" aria-labelledby="aitx-gap">
          <Container>
            <AnimateOnScroll isVisible={isVisible('gap-header')} data-animate-id="gap-header">
              <CenteredHeader sx={{ mb: { xs: 4, md: 6 } }}>
                <SectionEyebrow>The gap</SectionEyebrow>
                <SectionTitle component="h2" id="aitx-gap">
                  Most firms have AI pilots. Few have AI in <em>daily work</em>.
                </SectionTitle>
                <SectionSubtitle sx={{ maxWidth: 720 }}>{GAP_BODY}</SectionSubtitle>
              </CenteredHeader>
            </AnimateOnScroll>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: { xs: 1.5, md: 2.5 },
                maxWidth: 760,
                mx: 'auto',
              }}
            >
              {GAP_FIGURES.map(({ value, label, sub }, i) => (
                <AnimateOnScroll
                  key={value}
                  isVisible={isVisible(`gap-stat-${i}`)}
                  delay={0.05 * (i + 1)}
                  data-animate-id={`gap-stat-${i}`}
                >
                  <StatCard>
                    <StatValue as="div">{value}</StatValue>
                    <StatLabel>{label}</StatLabel>
                    <StatSublabel>{sub}</StatSublabel>
                  </StatCard>
                </AnimateOnScroll>
              ))}
            </Box>
          </Container>
        </Section>

        {/* ================================================================
            WHAT WE TRANSFORM. Six tiles on the landing page's dashed grid.
            ================================================================ */}
        <Section id="what-we-transform" withGrid aria-labelledby="aitx-transform">
          <Container>
            <AnimateOnScroll isVisible={isVisible('transform-header')} data-animate-id="transform-header">
              <CenteredHeader sx={{ mb: { xs: 4, md: 6 } }}>
                <SectionEyebrow>What we transform</SectionEyebrow>
                <SectionTitle component="h2" id="aitx-transform">
                  AI in the work your teams <em>already do</em>.
                </SectionTitle>
              </CenteredHeader>
            </AnimateOnScroll>
            <AnimateOnScroll isVisible={isVisible('transform-grid')} delay={0.1} data-animate-id="transform-grid">
              {/* Each tile draws its own right and bottom dashes and is pulled
                  1px under its neighbours; the frame clips the outer ones, so
                  the dividers hold at one, two, or three columns. */}
              <Box
                component="ul"
                sx={{
                  listStyle: 'none',
                  m: 0,
                  p: 0,
                  maxWidth: 1100,
                  mx: 'auto',
                  borderRadius: 2,
                  border: `1px dashed ${alpha(theme.palette.text.primary, 0.16)}`,
                  backgroundColor: alpha('#ffffff', 0.5),
                  overflow: 'hidden',
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                }}
              >
                {TRANSFORM.map(({ Icon, title, body }) => (
                  <Box
                    component="li"
                    key={title}
                    sx={{
                      p: { xs: 2.5, md: 3.5 },
                      mr: '-1px',
                      mb: '-1px',
                      borderRight: `1px dashed ${alpha(theme.palette.text.primary, 0.16)}`,
                      borderBottom: `1px dashed ${alpha(theme.palette.text.primary, 0.16)}`,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <Box aria-hidden="true" sx={chipSx}>
                      <Icon sx={{ fontSize: { xs: 20, md: 22 } }} />
                    </Box>
                    <Box>
                      <Typography component="h3" sx={cardTitleSx}>{title}</Typography>
                      <Typography sx={cardBodySx}>{body}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </AnimateOnScroll>
          </Container>
        </Section>

        {/* ================================================================
            HOW IT WORKS. Three phases on one track.
            ================================================================ */}
        <Section id="how-it-works" aria-labelledby="aitx-how">
          <Container>
            <AnimateOnScroll isVisible={isVisible('how-header')} data-animate-id="how-header">
              <CenteredHeader sx={{ mb: { xs: 4, md: 7 } }}>
                <SectionEyebrow>How it works</SectionEyebrow>
                <SectionTitle component="h2" id="aitx-how">
                  Assess, build, <em>adopt</em>.
                </SectionTitle>
              </CenteredHeader>
            </AnimateOnScroll>
            <Box data-animate-id="phase-track" sx={{ maxWidth: 1100, mx: 'auto' }}>
              <PhaseTrack
                phases={PHASES}
                shown={isVisible('phase-track')}
                cardTitleSx={cardTitleSx}
                cardBodySx={cardBodySx}
              />
            </Box>
          </Container>
        </Section>

        {/* ================================================================
            WHY RESTACK. Five tabs: the landing page's icon list on the left,
            the active claim's detail on a card to the right.
            ================================================================ */}
        <Section id="why-restack" withGrid aria-labelledby="aitx-why">
          <Container>
            <AnimateOnScroll isVisible={isVisible('why-header')} data-animate-id="why-header">
              <CenteredHeader sx={{ mb: { xs: 4, md: 6 } }}>
                <SectionEyebrow>Why Restack</SectionEyebrow>
                <SectionTitle component="h2" id="aitx-why">
                  Why work with <em>Restack</em>.
                </SectionTitle>
              </CenteredHeader>
            </AnimateOnScroll>
            <AnimateOnScroll isVisible={isVisible('why-tabs')} delay={0.1} data-animate-id="why-tabs">
              <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
                <WhyTabs items={WHY} chipSx={chipSx} cardBodySx={cardBodySx} />
              </Box>
            </AnimateOnScroll>
          </Container>
        </Section>

        {/* ================================================================
            WHO IT'S FOR
            ================================================================ */}
        <Section id="who-its-for" aria-labelledby="aitx-who">
          <Container>
            <AnimateOnScroll isVisible={isVisible('who-header')} data-animate-id="who-header">
              <CenteredHeader sx={{ mb: { xs: 4, md: 6 } }}>
                <SectionEyebrow>Who it&rsquo;s for</SectionEyebrow>
                <SectionTitle component="h2" id="aitx-who">
                  Who we <em>work with</em>.
                </SectionTitle>
              </CenteredHeader>
            </AnimateOnScroll>
            <AnimateOnScroll isVisible={isVisible('who-cards')} delay={0.1} data-animate-id="who-cards">
              {/* Three then two, centred, rather than a grid that strands
                  the last two cards on the left. */}
              <Box
                component="ul"
                sx={{
                  listStyle: 'none',
                  m: 0,
                  p: 0,
                  maxWidth: 1100,
                  mx: 'auto',
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: { xs: 1.5, md: 2.5 },
                }}
              >
                {AUDIENCES.map(({ title, body }) => (
                  <Box
                    component="li"
                    key={title}
                    sx={{
                      flex: {
                        xs: '1 1 100%',
                        sm: '0 1 calc((100% - 12px) / 2)',
                        md: '0 1 calc((100% - 40px) / 3)',
                      },
                      minWidth: 0,
                    }}
                  >
                    <BaseCard sx={{ p: { xs: 3, md: 3.5 }, ...STATIC_CARD }}>
                      <Typography component="h3" sx={cardTitleSx}>{title}</Typography>
                      <Typography sx={cardBodySx}>{body}</Typography>
                    </BaseCard>
                  </Box>
                ))}
              </Box>
              <Typography sx={{ ...cardBodySx, textAlign: 'center', mt: { xs: 3, md: 4 } }}>
                Also for PE deal teams: AI due diligence on acquisition targets, using our{' '}
                <MaybeLink href={LINKS.aiTargets}>five-check framework</MaybeLink>.
              </Typography>
            </AnimateOnScroll>
          </Container>
        </Section>

        {/* ================================================================
            TRANSFORMATION WORK BEHIND RESTACK. The founder's earlier
            programs, attributed by the subtitle, as text cards. Then the
            published work, as a row.
            ================================================================ */}
        <Section id="track-record" withGrid aria-labelledby="aitx-track">
          <Container>
            <AnimateOnScroll isVisible={isVisible('track-header')} data-animate-id="track-header">
              <CenteredHeader sx={{ mb: { xs: 4, md: 6 } }}>
                <SectionEyebrow>Track record</SectionEyebrow>
                <SectionTitle component="h2" id="aitx-track">
                  Transformation work behind <em>Restack</em>
                </SectionTitle>
                <SectionSubtitle sx={{ maxWidth: 640 }}>{TRACK_RECORD_SUBTITLE}</SectionSubtitle>
              </CenteredHeader>
            </AnimateOnScroll>

            <Box sx={{ maxWidth: 1100, mx: 'auto', display: 'flex', flexDirection: 'column', gap: { xs: 5, md: 7 } }}>
              <AnimateOnScroll isVisible={isVisible('track-rb')} delay={0.05} data-animate-id="track-rb">
                <Typography component="h3" sx={groupLabelSx}>At Roland Berger&rsquo;s AI practice</Typography>
                <CaseGrid items={ROLAND_BERGER_WORK} columns={3} cardTitleSx={cardTitleSx} cardBodySx={cardBodySx} />
              </AnimateOnScroll>

              <AnimateOnScroll isVisible={isVisible('track-earlier')} delay={0.05} data-animate-id="track-earlier">
                <Typography component="h3" sx={groupLabelSx}>Earlier transformation work</Typography>
                <CaseGrid items={EARLIER_WORK} columns={4} cardTitleSx={cardTitleSx} cardBodySx={cardBodySx} />
              </AnimateOnScroll>

              <AnimateOnScroll isVisible={isVisible('track-published')} delay={0.05} data-animate-id="track-published">
                <Typography component="h3" sx={groupLabelSx}>Published work</Typography>
                <Box
                  component="ul"
                  sx={{
                    listStyle: 'none',
                    m: 0,
                    p: 0,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                    gap: { xs: 1.5, md: 2.5 },
                  }}
                >
                  {PUBLISHED.map((card) => (
                    <Box component="li" key={card.title} sx={{ display: 'flex' }}>
                      <PublishedCard {...card} cardTitleSx={cardTitleSx} cardBodySx={cardBodySx} />
                    </Box>
                  ))}
                </Box>
              </AnimateOnScroll>
            </Box>
          </Container>
        </Section>

        {/* ================================================================
            QUESTIONS. The landing page's FAQ treatment.
            ================================================================ */}
        <Section id="faq" aria-labelledby="aitx-faq">
          <Container>
            <AnimateOnScroll isVisible={isVisible('faq-header')} data-animate-id="faq-header">
              <CenteredHeader>
                <SectionEyebrow>Questions</SectionEyebrow>
                <SectionTitle component="h2" id="aitx-faq">
                  Common <em>questions</em>.
                </SectionTitle>
              </CenteredHeader>
            </AnimateOnScroll>
            <AnimateOnScroll isVisible={isVisible('faq-list')} data-animate-id="faq-list">
              <Box sx={{ maxWidth: 760, mx: 'auto' }}>
                {FAQ.map(({ question, answer, link }) => (
                  <Accordion
                    key={question}
                    disableGutters
                    elevation={0}
                    square
                    sx={{
                      backgroundColor: 'transparent',
                      borderBottom: `1px solid ${inkA(0.08)}`,
                      '&::before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreRounded sx={{ color: inkA(0.45) }} />}
                      sx={{
                        px: 0.5,
                        minHeight: 52,
                        '& .MuiAccordionSummary-content': { my: 1 },
                        '&:hover': { backgroundColor: accentA(0.03) },
                        '&.Mui-focusVisible': { backgroundColor: 'transparent' },
                        '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: '-2px', borderRadius: '8px' },
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: font,
                          fontSize: '0.92rem',
                          fontWeight: 600,
                          letterSpacing: '-0.01em',
                          color: 'text.primary',
                        }}
                      >
                        {question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0.5, pt: 0, pb: 2 }}>
                      <Typography sx={{ fontFamily: font, fontSize: '0.84rem', lineHeight: 1.6, color: 'text.secondary' }}>
                        {answer}
                        {link && (
                          <>
                            {' '}{link.lead}
                            <Box component={RouterLink} to={link.to} sx={inlineLinkSx}>{link.label}</Box>.
                          </>
                        )}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </AnimateOnScroll>
          </Container>
        </Section>

        {/* ================================================================
            CLOSING BAND + form. The landing page's closing CTA treatment,
            and the only intake on the page.
            ================================================================ */}
        <Box
          component="section"
          id="form"
          aria-labelledby="aitx-start"
          sx={{
            position: 'relative',
            borderTop: `1px solid ${hair}`,
            background: `radial-gradient(ellipse 70% 120% at 50% 0%, ${accentA(0.06)} 0%, transparent 60%)`,
            px: 2,
            py: { xs: 7, md: 10 },
            scrollMarginTop: { xs: '64px', md: '72px' },
          }}
        >
          <AnimateOnScroll isVisible={isVisible('closing-cta')} data-animate-id="closing-cta">
            <Box sx={{ maxWidth: 560, mx: 'auto' }}>
              <Typography
                component="h2"
                id="aitx-start"
                sx={{
                  fontFamily: font,
                  fontSize: { xs: '1.9rem', md: '2.6rem' },
                  fontWeight: 600,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.08,
                  color: 'text.primary',
                  textAlign: 'center',
                  mb: { xs: 3.5, md: 4.5 },
                  '& em': { fontStyle: 'normal', fontWeight: 700, color: ACCENT },
                }}
              >
                Tell us where AI <em>is stuck</em>.
              </Typography>

              <BaseCard
                sx={{
                  p: { xs: 3, md: 4 },
                  height: 'auto',
                  ...STATIC_CARD,
                }}
              >
                {status === 'sent' ? (
                  <Box role="status" sx={{ py: 1 }}>
                    <Typography sx={{ ...cardTitleSx, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckRounded aria-hidden="true" sx={{ color: '#1B7A4F', fontSize: '1.25rem' }} />
                      Got it.
                    </Typography>
                    <Typography sx={cardBodySx}>We will reply today with times for a scoping call.</Typography>
                  </Box>
                ) : (
                  // noValidate so our own per-field messages show instead of
                  // the browser bubble, which is not stylable and vanishes on
                  // the next click.
                  <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormField
                      id="aitx-name"
                      label="Name"
                      inputRef={bindField('name')}
                      autoComplete="name"
                      value={form.name}
                      onChange={onField('name')}
                      error={fieldErrors.name}
                    />
                    <FormField
                      id="aitx-email"
                      label="Work email"
                      type="email"
                      inputRef={bindField('email')}
                      autoComplete="email"
                      value={form.email}
                      onChange={onField('email')}
                      error={fieldErrors.email}
                    />
                    <FormField
                      id="aitx-company"
                      label="Company"
                      inputRef={bindField('company')}
                      autoComplete="organization"
                      value={form.company}
                      onChange={onField('company')}
                      error={fieldErrors.company}
                    />
                    <FormField
                      id="aitx-stuck"
                      label="Where is AI stuck for you?"
                      multiline
                      inputRef={bindField('stuck')}
                      value={form.stuck}
                      onChange={onField('stuck')}
                      error={fieldErrors.stuck}
                    />

                    {submitError && (
                      <Box component="p" role="alert" sx={errorTextSx}>{submitError}</Box>
                    )}

                    <PrimaryCta type="submit" fullWidth busy={status === 'submitting'} />
                  </Box>
                )}
              </BaseCard>

              <Typography sx={{ ...cardBodySx, textAlign: 'center', mt: 3, px: { xs: 1, md: 4 } }}>
                We reply the same day with times for a scoping call. Fixed fee per phase,
                agreed first. Prefer to start smaller? Ask for the free two-page note.
              </Typography>
            </Box>
          </AnimateOnScroll>
        </Box>
      </Box>

      <Footer />
    </LandingContainer>
  );
}

// =============================================================================
// Pieces
// =============================================================================

const inlineLinkSx = {
  color: ACCENT,
  fontWeight: 500,
  textDecoration: 'underline',
  textDecorationColor: accentA(0.35),
  textUnderlineOffset: '3px',
  borderRadius: '4px',
  transition: 'text-decoration-color 0.18s ease, color 0.18s ease',
  '&:hover': { color: '#A63419', textDecorationColor: '#A63419' },
  '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
};

const errorTextSx = { fontSize: '0.8125rem', lineHeight: 1.4, color: ACCENT, m: 0 };

// Visually hidden, still announced.
const SR_ONLY = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/** An external link when the URL is known, plain text when it is not. */
function MaybeLink({ href, children }) {
  if (!href) return <>{children}</>;
  return (
    <Box component="a" href={href} target="_blank" rel="noopener noreferrer" sx={inlineLinkSx}>
      {children}
      <Box component="span" sx={SR_ONLY}> (opens in a new tab)</Box>
    </Box>
  );
}

/**
 * The one primary action, "Book a scoping call", in the landing page's dark
 * pill: a scroll button in the hero, the submit button in the form.
 */
function PrimaryCta({ onClick, type = 'button', fullWidth = false, busy = false }) {
  return (
    <Button
      type={type}
      fullWidth={fullWidth}
      onClick={onClick}
      disabled={busy}
      disableElevation
      sx={{
        height: 48,
        px: { xs: 2.75, md: 3.25 },
        borderRadius: '999px',
        fontWeight: 600,
        fontSize: { xs: '0.85rem', md: '0.9rem' },
        letterSpacing: '-0.005em',
        lineHeight: 1.45,
        whiteSpace: 'nowrap',
        textTransform: 'none',
        color: '#ffffff',
        backgroundColor: INK,
        border: 'none',
        transition: 'background-color 0.2s ease, box-shadow 0.2s ease, transform 0.12s ease',
        '&:hover': { backgroundColor: '#1A0A04', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)' },
        '&:active': { transform: 'translateY(1px)' },
        '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 3 },
        '&.Mui-disabled': { color: alpha('#ffffff', 0.8), backgroundColor: inkA(0.55) },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:active': { transform: 'none' } },
      }}
    >
      {busy ? 'Sending' : 'Book a scoping call'}
    </Button>
  );
}

/** A labelled input with its inline error. Labels stay visible: a placeholder is not a label. */
function FormField({ id, label, type = 'text', multiline = false, inputRef, autoComplete, value, onChange, error }) {
  const errorId = `${id}-error`;
  return (
    <Box>
      <Box
        component="label"
        htmlFor={id}
        sx={{ display: 'block', mb: 1, fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' }}
      >
        {label}
      </Box>
      <Box
        component={multiline ? 'textarea' : 'input'}
        id={id}
        ref={inputRef}
        type={multiline ? undefined : type}
        rows={multiline ? 4 : undefined}
        name={id}
        autoComplete={autoComplete}
        required
        value={value}
        onChange={onChange}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? errorId : undefined}
        sx={{
          width: '100%',
          boxSizing: 'border-box',
          // Safari and iOS ignore border-radius on native inputs unless
          // appearance is reset.
          appearance: 'none',
          WebkitAppearance: 'none',
          fontFamily: 'inherit',
          fontSize: '1rem',
          lineHeight: 1.5,
          color: INK,
          px: 1.5,
          py: 1.5,
          borderRadius: '12px',
          border: `1px solid ${error ? ACCENT : inkA(0.18)}`,
          backgroundColor: '#fff',
          outline: 'none',
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          ...(multiline ? { resize: 'vertical', minHeight: 112 } : {}),
          '&:hover': { borderColor: error ? ACCENT : inkA(0.3) },
          '&:focus': { borderColor: ACCENT, boxShadow: `0 0 0 3px ${accentA(0.14)}` },
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      />
      {error && (
        <Box component="p" id={errorId} sx={{ ...errorTextSx, mt: 1 }}>{error}</Box>
      )}
    </Box>
  );
}

/**
 * Assess, build, adopt on one track. Desktop draws a hairline across the top
 * of the three columns with a node at the head of each; the line fills in once
 * the track reaches the fold. Mobile stacks the phases on a vertical rail.
 */
function PhaseTrack({ phases, shown, cardTitleSx, cardBodySx }) {
  return (
    <Box
      component="ol"
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
        gap: { xs: 4, md: 5 },
      }}
    >
      {/* The track. Horizontal on desktop, vertical on mobile. */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          backgroundColor: inkA(0.1),
          top: { xs: 8, md: 7 },
          left: { xs: 7, md: 0 },
          right: { xs: 'auto', md: 0 },
          bottom: { xs: 8, md: 'auto' },
          width: { xs: '1px', md: 'auto' },
          height: { xs: 'auto', md: '1px' },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundColor: ACCENT,
            transformOrigin: { xs: 'top', md: 'left' },
            transform: shown ? 'none' : { xs: 'scaleY(0)', md: 'scaleX(0)' },
            transition: 'transform 1.1s cubic-bezier(0.22,1,0.36,1) 0.15s',
            '@media (prefers-reduced-motion: reduce)': { ...STILL },
          },
        }}
      />
      {phases.map(({ name, meta, body }, i) => (
        <Box
          component="li"
          key={name}
          sx={{
            position: 'relative',
            pl: { xs: 5, md: 0 },
            pt: { xs: 0, md: 5 },
            opacity: shown ? 1 : 0,
            transform: shown ? 'none' : 'translateY(14px)',
            transition: `opacity 0.45s cubic-bezier(0.22,1,0.36,1) ${0.2 + i * 0.18}s, transform 0.45s cubic-bezier(0.22,1,0.36,1) ${0.2 + i * 0.18}s`,
            '@media (prefers-reduced-motion: reduce)': { opacity: 1, ...STILL },
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: 15,
              height: 15,
              borderRadius: '50%',
              backgroundColor: PAPER,
              border: `2px solid ${ACCENT}`,
              boxSizing: 'border-box',
            }}
          />
          <Typography component="h3" sx={{ ...cardTitleSx, fontSize: { xs: '1.25rem', md: '1.5rem' }, letterSpacing: '-0.02em' }}>
            {name}
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: ACCENT, mb: 1.5 }}>
            {meta}
          </Typography>
          <Typography sx={cardBodySx}>{body}</Typography>
        </Box>
      ))}
    </Box>
  );
}

const panelIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: none; }
`;

/**
 * "Why Restack" as five tabs: the claims on the left in the landing page's
 * icon-list style, the active one's detail on a card to the right. A real ARIA
 * tab set: arrow keys, Home and End move between tabs, and only the active tab
 * is in the tab order.
 */
function WhyTabs({ items, chipSx, cardBodySx }) {
  const [active, setActive] = useState(0);
  const tabRefs = useRef([]);
  const last = items.length - 1;

  const onKeyDown = (e, i) => {
    let next = null;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = i === last ? 0 : i + 1;
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = i === 0 ? last : i - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;
    e.preventDefault();
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  const current = items[active];

  return (
    <Box
      sx={{
        display: 'grid',
        // minmax(0, ...) so a visual's min-content width can never push the
        // grid past a phone screen.
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 0.9fr) minmax(0, 1.1fr)' },
        gap: { xs: 3, md: 6 },
        alignItems: 'start',
      }}
    >
      <Box role="tablist" aria-label="Why Restack" aria-orientation="vertical" sx={{ display: 'flex', flexDirection: 'column' }}>
        {items.map(({ Icon, title }, i) => {
          const selected = i === active;
          return (
            <Box
              key={title}
              component="button"
              type="button"
              role="tab"
              id={`aitx-why-tab-${i}`}
              aria-selected={selected}
              aria-controls="aitx-why-panel"
              tabIndex={selected ? 0 : -1}
              ref={(el) => { tabRefs.current[i] = el; }}
              onClick={() => setActive(i)}
              onKeyDown={(e) => onKeyDown(e, i)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 2, md: 2.5 },
                width: '100%',
                textAlign: 'left',
                border: 'none',
                borderBottom: i === last ? 'none' : `1px solid ${inkA(0.08)}`,
                background: 'transparent',
                cursor: 'pointer',
                py: { xs: 2, md: 2.5 },
                px: 1,
                borderRadius: 0,
                fontFamily: 'inherit',
                fontWeight: 600,
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.35,
                color: selected ? 'text.primary' : inkA(0.5),
                transition: 'color 0.2s ease, background-color 0.2s ease',
                '& .aitx-why__chip': {
                  backgroundColor: selected ? ACCENT : accentA(0.1),
                  color: selected ? '#ffffff' : ACCENT,
                  transition: 'background-color 0.2s ease, color 0.2s ease',
                },
                '&:hover': { color: 'text.primary', backgroundColor: accentA(0.03) },
                '&:active': { transform: 'translateY(1px)' },
                '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: '-2px', borderRadius: '8px' },
                '@media (prefers-reduced-motion: reduce)': {
                  transition: 'none',
                  '& .aitx-why__chip': { transition: 'none' },
                  '&:active': { transform: 'none' },
                },
              }}
            >
              <Box aria-hidden="true" className="aitx-why__chip" sx={chipSx}>
                <Icon sx={{ fontSize: { xs: 20, md: 22 } }} />
              </Box>
              {title}
            </Box>
          );
        })}
      </Box>

      <BaseCard
        role="tabpanel"
        id="aitx-why-panel"
        aria-labelledby={`aitx-why-tab-${active}`}
        tabIndex={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          height: 'auto',
          outline: 'none',
          ...STATIC_CARD,
          '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
        }}
      >
        <Box
          key={active}
          sx={{
            animation: `${panelIn} 0.45s cubic-bezier(0.22,1,0.36,1)`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        >
          <WhyVisual kind={current.visual} />
          <Typography sx={{ ...cardBodySx, fontSize: { xs: '0.9375rem', md: '1rem' }, mt: { xs: 2.5, md: 3 } }}>
            {current.body}
          </Typography>
          {current.visual === 'agents' && <AppSourceBadge height={40} sx={{ mt: 2.5 }} />}
        </Box>
      </BaseCard>
    </Box>
  );
}

/** One small, decorative illustration per "Why Restack" tab. Text carries the meaning. */
function WhyVisual({ kind }) {
  const frame = {
    borderRadius: '12px',
    border: `1px solid ${inkA(0.08)}`,
    backgroundColor: PAPER,
    p: { xs: 2, md: 3 },
    minHeight: { xs: 184, md: 216 },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 1.5,
  };
  const row = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 1,
    p: 1.5,
    borderRadius: '10px',
    backgroundColor: '#fff',
    border: `1px solid ${inkA(0.06)}`,
  };
  const label = { fontSize: '0.875rem', fontWeight: 500, color: INK };
  const muted = { fontSize: '0.75rem', fontWeight: 500, color: inkA(0.5) };
  const chip = (color) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    px: 1.5,
    py: 0.5,
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 600,
    color,
    backgroundColor: alpha(color, 0.08),
    whiteSpace: 'nowrap',
  });

  if (kind === 'agents') {
    return (
      <Box aria-hidden="true" sx={frame}>
        {['PowerPoint', 'Excel', 'Word'].map((app) => (
          <Box key={app} sx={row}>
            <Box sx={label}>Restack for {app}</Box>
            <Box sx={chip('#1B7A4F')}>
              <CheckRounded sx={{ fontSize: '0.875rem' }} />
              Live
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (kind === 'inFile') {
    return (
      <Box aria-hidden="true" sx={{ ...frame, p: 0, overflow: 'hidden', justifyContent: 'stretch', gap: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, backgroundColor: ACCENT }}>
          {[0, 1, 2].map((d) => (
            <Box key={d} sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: alpha('#fff', 0.45) }} />
          ))}
          <Box sx={{ ...muted, color: alpha('#fff', 0.9), ml: 1 }}>Board_pack.pptx</Box>
        </Box>
        <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 42%', minHeight: { xs: 152, md: 184 } }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'flex-end', gap: 1, borderRight: `1px solid ${inkA(0.06)}` }}>
            {[38, 54, 46, 70, 84].map((h, i) => (
              <Box
                key={i}
                sx={{ flex: 1, height: `${h}%`, borderRadius: '4px 4px 0 0', backgroundColor: i === 4 ? ACCENT : accentA(0.22) }}
              />
            ))}
          </Box>
          <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, backgroundColor: '#fff' }}>
            <Box sx={{ ...muted, color: ACCENT, fontWeight: 600 }}>Restack</Box>
            <Box sx={{ ...muted, color: INK, p: 1, borderRadius: '8px', backgroundColor: inkA(0.05), lineHeight: 1.4 }}>
              Update the margin chart from the model
            </Box>
            <Box sx={{ ...muted, lineHeight: 1.4, display: 'flex', gap: 0.5 }}>
              <CheckRounded sx={{ fontSize: '0.875rem', color: '#1B7A4F', flexShrink: 0 }} />
              Read Model.xlsx, updated the chart
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  if (kind === 'adoption') {
    const stages = [
      { name: 'Research', state: 'AI in use', ok: true },
      { name: 'First drafts', state: 'AI in use', ok: true },
      { name: 'Into Excel and PowerPoint', state: 'Copied by hand', ok: false },
    ];
    return (
      <Box aria-hidden="true" sx={frame}>
        {stages.map(({ name, state, ok }) => (
          <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                flexShrink: 0,
                borderRadius: '50%',
                backgroundColor: ok ? '#1B7A4F' : 'transparent',
                border: ok ? 'none' : `2px dashed ${ACCENT}`,
                boxSizing: 'border-box',
              }}
            />
            <Box
              sx={{
                ...row,
                flex: 1,
                minWidth: 0,
                border: `1px ${ok ? 'solid' : 'dashed'} ${ok ? inkA(0.06) : accentA(0.45)}`,
              }}
            >
              <Box sx={label}>{name}</Box>
              <Box sx={chip(ok ? '#1B7A4F' : ACCENT)}>{state}</Box>
            </Box>
          </Box>
        ))}
        <Box sx={{ ...muted, pl: 3.5 }}>Where AI stalls, and where we design from the first week</Box>
      </Box>
    );
  }

  if (kind === 'models') {
    return (
      <Box aria-hidden="true" sx={{ ...frame, alignItems: 'center', gap: 2 }}>
        <Box sx={{ ...label, px: 2, py: 1, borderRadius: '10px', backgroundColor: '#fff', border: `1px solid ${inkA(0.08)}` }}>
          Your workflow
        </Box>
        <Box sx={{ width: '1px', height: 24, backgroundColor: inkA(0.2) }} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
          {['Claude', 'GPT', 'Open models'].map((m) => (
            <Box
              key={m}
              sx={{ ...label, px: 2, py: 1, borderRadius: '999px', backgroundColor: '#fff', border: `1px solid ${inkA(0.12)}` }}
            >
              {m}
            </Box>
          ))}
        </Box>
        <Box sx={muted}>Chosen per workflow</Box>
      </Box>
    );
  }

  // 'fee'
  return (
    <Box aria-hidden="true" sx={frame}>
      {['Assess', 'Build', 'Adopt'].map((phase) => (
        <Box key={phase} sx={row}>
          <Box sx={label}>{phase}</Box>
          <Box sx={chip(ACCENT)}>Fixed fee</Box>
        </Box>
      ))}
      <Box sx={{ ...muted, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CheckRounded sx={{ fontSize: '0.875rem', color: '#1B7A4F' }} />
        Agreed before work starts
      </Box>
    </Box>
  );
}

/**
 * The founder's programs as text cards: where it was done (when the group
 * label does not already say), the program, its scale, one line on the work.
 * Centred rows, so a short last row sits in the middle rather than stranded
 * on the left.
 */
function CaseGrid({ items, columns, cardTitleSx, cardBodySx }) {
  const gapPx = { xs: 12, md: 20 };
  const basis = (n, gap) => `0 1 calc((100% - ${(n - 1) * gap}px) / ${n})`;
  return (
    <Box
      component="ul"
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: { xs: `${gapPx.xs}px`, md: `${gapPx.md}px` },
      }}
    >
      {items.map(({ org, name, scale, work }) => (
        <Box
          component="li"
          key={name}
          sx={{
            minWidth: 0,
            display: 'flex',
            flex: {
              xs: '1 1 100%',
              sm: basis(2, gapPx.xs),
              md: basis(Math.min(columns, 3), gapPx.md),
              lg: basis(columns, gapPx.md),
            },
          }}
        >
          <BaseCard
            sx={{
              p: { xs: 2.5, md: 3 },
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              ...STATIC_CARD,
            }}
          >
            {org && (
              <Box sx={{ fontSize: '0.75rem', fontWeight: 600, color: ACCENT, mb: 1.5 }}>{org}</Box>
            )}
            <Typography component="h4" sx={{ ...cardTitleSx, fontSize: { xs: '1rem', md: '1.0625rem' } }}>
              {name}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '0.875rem', md: '0.9375rem' },
                fontWeight: 700,
                letterSpacing: '-0.01em',
                color: ACCENT,
                mb: 1,
              }}
            >
              {scale}
            </Typography>
            <Typography sx={cardBodySx}>{work}</Typography>
          </BaseCard>
        </Box>
      ))}
    </Box>
  );
}

/** A published-work card. A link out when the URL is known; a plain card when it is not. */
function PublishedCard({ kind, title, body, action, href, cardTitleSx, cardBodySx }) {
  const linked = Boolean(href);
  return (
    <BaseCard
      component={linked ? 'a' : 'div'}
      {...(linked ? { href, target: '_blank', rel: 'noopener noreferrer' } : {})}
      sx={{
        p: { xs: 2.5, md: 3 },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        textDecoration: 'none',
        color: 'inherit',
        ...(linked
          ? {
            '&:hover .aitx-published__go': { gap: 1 },
            '&:active': { transform: 'translateY(0)' },
            '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 3 },
          }
          : STATIC_CARD),
      }}
    >
      <Box sx={{ fontSize: '0.75rem', fontWeight: 600, color: ACCENT, mb: 1.5 }}>{kind}</Box>
      <Typography component="h4" sx={{ ...cardTitleSx, fontSize: { xs: '1rem', md: '1.0625rem' } }}>{title}</Typography>
      <Typography sx={{ ...cardBodySx, flex: 1 }}>{body}</Typography>
      {linked && (
        <Box
          className="aitx-published__go"
          sx={{
            mt: 2.5,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '0.875rem',
            fontWeight: 600,
            color: ACCENT,
            transition: 'gap 0.2s ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        >
          {action}
          <NorthEastRounded aria-hidden="true" sx={{ fontSize: '0.875rem' }} />
          <Box component="span" sx={SR_ONLY}> (opens in a new tab)</Box>
        </Box>
      )}
    </BaseCard>
  );
}

// =============================================================================
// Scrolling
// =============================================================================

/** Nearest scrollable ancestor, or null when the window is the scroller. */
function scrollParent(el) {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return null;
}

/**
 * Scroll `el` to the top of its scroller, `offset` px clear of the fixed
 * header, and call `onDone` when it lands.
 *
 * Driven frame by frame rather than scrollIntoView({behavior:'smooth'}): the
 * marketing pages scroll inside LandingContainer, and a native smooth scroll
 * there is cancelled part-way by the re-renders the scroll itself triggers
 * (the header compressing, reveals flipping). A deliberate wheel or touch from
 * the reader still wins and hands control straight back.
 */
function scrollToElement(el, { offset = 72, duration = 560, onDone } = {}) {
  const scroller = scrollParent(el);
  const reduce = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const readTop = () => (scroller ? scroller.scrollTop : window.scrollY || 0);
  const writeTop = (v) => {
    if (scroller) scroller.scrollTop = v;
    else window.scrollTo(0, v);
  };
  const maxTop = scroller
    ? scroller.scrollHeight - scroller.clientHeight
    : document.documentElement.scrollHeight - window.innerHeight;

  const from = readTop();
  const to = Math.max(0, Math.min(maxTop, from + el.getBoundingClientRect().top - offset));

  if (reduce || Math.abs(to - from) < 2) {
    writeTop(to);
    onDone?.();
    return;
  }

  const target = scroller || window;
  let cancelled = false;
  let settled = false;
  let guard = 0;

  const teardown = () => {
    window.clearTimeout(guard);
    target.removeEventListener('wheel', abort);
    target.removeEventListener('touchstart', abort);
  };
  function abort() {
    cancelled = true;
    teardown();
  }
  const finish = (snap) => {
    if (settled) return;
    settled = true;
    teardown();
    if (snap) writeTop(to);
    onDone?.();
  };

  target.addEventListener('wheel', abort, { passive: true });
  target.addEventListener('touchstart', abort, { passive: true });

  // requestAnimationFrame can stop firing when the page is not painting
  // (background tab). If the frames have not finished the job shortly after
  // they should have, land it outright.
  guard = window.setTimeout(() => {
    if (!cancelled) finish(true);
  }, duration + 300);

  const start = performance.now();
  const step = (now) => {
    if (cancelled || settled) return;
    const t = Math.min((now - start) / duration, 1);
    writeTop(from + (to - from) * (1 - Math.pow(1 - t, 3)));
    if (t < 1) requestAnimationFrame(step);
    else finish(false);
  };
  requestAnimationFrame(step);
}
